import './style.css';
import { CafeAudio } from './audio';
import { badgeIconHtml, catSvg, cookieJarSvg, mathVisual, servingPlateSvg, sleepingCatSvg } from './art';
import { AVATARS, OP_INFO, factStatKey, makeId, newPlayer, type InputMode, type Operation, type Player, type Question, type RoundState } from './model';
import { BADGES, CATS, DECORATIONS, ROUND_TOKEN_REWARD, commitRoundRewards, badgeById, cafeCustomerCats, catBookPages, decorationById, purchaseDecoration, recordIndependentFact, toggleDecoration, unlockBadges, type DecorationDefinition, type DecorationSlot } from './rewards';
import { avoidRepeatedMenuFood, buildOperationPlan, choiceAnswers, explanationFor, generateQuestion } from './questions';
import { equationHtml, equationWidthClass, highlightedStoryHtml } from './presentation';
import { StorageProblem, addPlayer, deletePlayer, listPlayers, loadIndex, loadPlayer, savePlayer, setActivePlayer, validateImportedPlayer } from './storage';

const app = document.querySelector<HTMLDivElement>('#app')!;
const toast = document.querySelector<HTMLDivElement>('#toast')!;
const audio = new CafeAudio();
let player: Player | null = null;
let screen: 'lobby' | 'round' | 'summary' = 'lobby';
let modal: 'settings' | 'stickers' | 'shop' | 'customize' | 'players' | 'grownups' | null = null;
let feedback: 'none' | 'wrong' | 'help' | 'serving' = 'none';
let keypadValue = '';
let stickerPage = 0;
let collectionTab: 'cats' | 'badges' = 'cats';
let shopFilter: 'all' | DecorationSlot = 'all';
let customizeSlot: DecorationSlot = 'wearable';
const shopActionLocks = new Set<string>();
const customizeActionLocks = new Set<string>();
let modalReturnAction: string | null = null;
let focusNewModal = false;
let processing = false;
let saveWarning = '';

const DECORATION_SLOTS: DecorationSlot[] = ['wearable', 'counter', 'wall', 'shelf', 'room', 'theme'];
const DECORATION_SLOT_NAMES: Record<DecorationSlot, string> = { wearable: 'Outfits', counter: 'Counter', wall: 'Posters', shelf: 'Shelf', room: 'Room', theme: 'Colors' };

function seededRng(seed: string): () => number {
  let state = [...seed].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 2166136261);
  return () => { state = (state * 1664525 + 1013904223) >>> 0; return state / 4294967296; };
}

function showToast(message: string): void {
  toast.textContent = message;
  toast.classList.add('show');
  window.setTimeout(() => toast.classList.remove('show'), 2300);
}

function safeSave(): boolean {
  if (!player) return false;
  try { savePlayer(player); saveWarning = ''; return true; }
  catch (error) { saveWarning = error instanceof Error ? error.message : 'Progress could not be saved.'; return false; }
}

function initialize(): void {
  try {
    const index = loadIndex();
    if (index.lastActivePlayerId) player = loadPlayer(index.lastActivePlayerId);
    player ??= listPlayers()[0] ?? null;
  } catch (error) {
    saveWarning = error instanceof Error ? error.message : 'Saved progress could not be read.';
  }
  if (!player) renderOnboarding();
  else {
    unlockBadges(player);
    safeSave();
    audio.applyPreference(player.settings.soundPreferred, player.settings.volume);
    if (player.round?.status === 'complete') screen = 'summary';
    render();
  }
}

function renderOnboarding(): void {
  app.innerHTML = `<main class="onboarding">
    <section class="welcome-card" aria-labelledby="welcome-title">
      <div class="brand-mark">${catSvg('marmalade', 'happy')}</div>
      <p class="eyebrow">Now serving tiny triumphs</p>
      <h1 id="welcome-title">Tables Café</h1>
      <p>Pick your café cat and a starting menu. You can change the math whenever you like.</p>
      ${saveWarning ? `<p class="warning">${saveWarning}</p>` : ''}
      <form id="onboarding-form">
        <label class="field">What should we call you?<input id="new-name" maxlength="24" autocomplete="nickname" required placeholder="Your name" /></label>
        <fieldset><legend>Choose your café cat</legend><div class="avatar-grid">${AVATARS.map((id, index) => `<label class="avatar-choice"><input type="radio" name="avatar" value="${id}" ${index === 0 ? 'checked' : ''}/><span>${catSvg(id)}</span></label>`).join('')}</div></fieldset>
        <fieldset><legend>Choose a starting menu</legend><div class="preset-grid">
          <label class="preset"><input type="radio" name="preset" value="little" checked/><span><strong>Little bites</strong><small>Addition · Within 10 · Tap choices</small></span></label>
          <label class="preset"><input type="radio" name="preset" value="tables"/><span><strong>Table time</strong><small>Multiplication · 2s · Tap choices</small></span></label>
        </div></fieldset>
        <button class="primary jumbo" type="submit">Open my café</button>
      </form>
    </section>
  </main>`;
  document.querySelector<HTMLFormElement>('#onboarding-form')!.addEventListener('submit', (event) => {
    event.preventDefault();
    const name = document.querySelector<HTMLInputElement>('#new-name')!.value;
    const avatar = new FormData(event.currentTarget as HTMLFormElement).get('avatar') as string;
    const preset = new FormData(event.currentTarget as HTMLFormElement).get('preset') as 'little' | 'tables';
    player = newPlayer(name, avatar, preset);
    try { addPlayer(player); } catch (error) { saveWarning = error instanceof Error ? error.message : 'Could not create the player.'; }
    screen = 'lobby'; render();
  });
}

function settingsSummary(p: Player): string {
  const ops = p.settings.operations.map((op) => OP_INFO[op].word).join(' + ');
  if (p.settings.operations.length === 1 && p.settings.operations[0] === 'multiply') return `${ops} · ${p.settings.multiplicationTables.join(', ')}s`;
  if (p.settings.operations.length === 1 && p.settings.operations[0] === 'divide') return `${ops} · ÷ ${p.settings.divisionFamilies.join(', ')}`;
  if (p.settings.operations.every((op) => op === 'add' || op === 'subtract')) return `${ops} · Within ${Math.max(p.settings.addLimit, p.settings.subtractLimit)}`;
  return ops;
}

function headerHtml(): string {
  return `<header class="topbar"><button class="brand-button" data-action="lobby" aria-label="Tables Café home"><span class="brand-avatar" data-brand-avatar>${catSvg(player!.avatar, 'happy')}</span><span>Tables Café</span></button>
    <nav aria-label="Café controls">
      <button class="soft" data-action="sound">${audio.enabled ? (audio.isMuted ? '🔇 Unmute' : '🔊 Mute') : '♪ Turn sound on'}</button>
      ${audio.enabled ? `<label class="header-volume"><span class="sr-only">Sound volume</span><input data-header-volume aria-label="Sound volume" type="range" min="0" max="1" step="0.05" value="${player!.settings.volume}"/></label>` : ''}
      <button class="soft player-pill" data-action="players"><span data-player-name></span> ▾</button>
      <button class="icon-button" data-action="settings" aria-label="Settings">⚙</button>
    </nav></header>`;
}

function cafeThemeClass(): string {
  const theme = decorationById(player?.equipped.theme);
  return theme?.slot === 'theme' ? `theme-${theme.id.slice(0, -6)}` : 'theme-classic';
}

function counterDecorationHtml(item: string | undefined): string {
  if (item === 'counter-plant') return '<div class="counter-decoration" aria-label="Plant">🪴</div>';
  if (item === 'cookie-jar') return `<div class="counter-decoration" aria-label="Cookie jar">${cookieJarSvg()}</div>`;
  if (item === 'flower-vase') return '<div class="counter-decoration" aria-label="Flower vase">💐</div>';
  if (item === 'teapot') return '<div class="counter-decoration" aria-label="Teapot">🫖</div>';
  if (item === 'cupcake-stand') return '<div class="counter-decoration counter-cupcake" aria-label="Cupcake stand"><span>🧁</span></div>';
  if (item === 'recipe-book') return '<div class="counter-decoration" aria-label="Recipe book">📖</div>';
  if (item === 'fruit-bowl') return '<div class="counter-decoration counter-fruit" aria-label="Fruit bowl">🍎<span>🍐</span></div>';
  return '';
}

function sceneDecorationsHtml(includeRoom = true): string {
  const equipped = player!.equipped;
  const rooms: Record<string, string> = {
    bunting: '<div class="scene-room-decoration game-bunting" aria-label="Bunting">▲ ● ▲ ● ▲</div>',
    'cafe-curtains': '<div class="scene-room-decoration cafe-curtains" aria-label="Café curtains"><i></i><i></i></div>',
    'hanging-plants': '<div class="scene-room-decoration hanging-plants" aria-label="Hanging plants"><span>🪴</span><span>🪴</span></div>',
    'string-lights': '<div class="scene-room-decoration string-lights" aria-label="String lights"><i></i><i></i><i></i><i></i><i></i><i></i></div>',
    'paper-lanterns': '<div class="scene-room-decoration paper-lanterns" aria-label="Paper lanterns"><span>●</span><span>●</span><span>●</span><span>●</span></div>',
  };
  const walls: Record<string, string> = {
    'number-poster': '2 · 4 · 6 · 8',
    'paw-poster': '● + ● = ♥',
    'bakery-menu': 'MENU<br>☕ · 🥐 · 🍪',
    'times-chalkboard': '3 × 4 = 12',
    'garden-print': '🌿 ✿ 🌱',
    'cat-portrait': '🐱',
  };
  const shelves: Record<string, string> = {
    'cat-stuffy': '🧸',
    'cookie-cushion': '🍪',
    cookbooks: '📚',
    'yarn-basket': '🧶',
    'pastry-plush': '🥐',
    'tiny-clock': '🕰️',
  };
  const room = includeRoom && equipped.room ? rooms[equipped.room] ?? '' : '';
  const wallName = decorationById(equipped.wall)?.name ?? '';
  const wall = equipped.wall && walls[equipped.wall] ? `<div class="game-wall-decoration wall-${equipped.wall}" aria-label="${wallName}">${walls[equipped.wall]}</div>` : '';
  const shelfName = decorationById(equipped.shelf)?.name ?? '';
  const shelf = equipped.shelf && shelves[equipped.shelf] ? `<div class="game-shelf-decoration shelf-${equipped.shelf}" aria-label="${shelfName}">${shelves[equipped.shelf]}</div>` : '';
  return `${room}${wall}${shelf}`;
}

function render(): void {
  if (!player) return renderOnboarding();
  const content = screen === 'round' ? roundHtml() : screen === 'summary' ? summaryHtml() : lobbyHtml();
  app.innerHTML = `${headerHtml()}${saveWarning ? `<div class="warning global-warning">${saveWarning}</div>` : ''}${content}${modal ? modalHtml(modal) : ''}`;
  document.querySelectorAll<HTMLElement>('[data-player-name]').forEach((node) => { node.textContent = player!.name; });
  wireCommon();
  if (screen === 'round') wireRound();
  if (modal) wireModal();
}

function openModal(kind: NonNullable<typeof modal>, returnAction?: string): void {
  if (returnAction) modalReturnAction = returnAction;
  modal = kind;
  focusNewModal = true;
  render();
}

function closeModal(): void {
  const returnAction = modalReturnAction;
  modal = null;
  focusNewModal = false;
  render();
  if (returnAction) window.requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-action="${returnAction}"]`)?.focus());
}

function lobbyHtml(): string {
  const active = player!.round?.status === 'active';
  const ownedDecorations = DECORATIONS.filter((item) => player!.cosmetics.includes(item.id)).length;
  const showingDecorations = DECORATION_SLOTS.filter((slot) => decorationById(player!.equipped[slot])?.slot === slot).length;
  const unownedDecorations = DECORATIONS.length - ownedDecorations;
  return `<main class="lobby"><section class="cafe-front ${cafeThemeClass()}">
    <button class="cafe-front-open" data-action="${active ? 'continue' : 'new-round'}" aria-label="${active ? 'Continue this round' : 'Open the café'}"></button>
    <div class="awning"><span></span><span></span><span></span><span></span><span></span></div>
    <div class="window-scene"><div class="sky"><i></i><i></i></div>${sceneDecorationsHtml()}<div class="window-cat">${catSvg(player!.avatar, 'happy', player!.equipped.wearable ?? '')}</div><button class="sleepy-cat" data-action="sleepy" aria-label="Tap the curled-up shape">${sleepingCatSvg()}</button></div>
    <div class="counter"><div><p class="eyebrow">Today’s menu</p><h1>Ready to serve?</h1><p class="math-reminder">${settingsSummary(player!)}</p></div>${counterDecorationHtml(player!.equipped.counter) || '<div class="cake-stand" aria-hidden="true">◒</div>'}</div>
  </section>
  <section class="lobby-actions">
    <p>Ten friendly customers. No timer. Pause whenever you like.</p>
    <button class="primary jumbo" data-action="${active ? 'continue' : 'new-round'}">${active ? 'Continue this round' : 'Open the café'}</button>
    ${active ? '<button class="text-button" data-action="new-round">Start a new round instead</button>' : ''}
    <div class="quick-links"><button class="tile-button" data-action="shop"><span>🪙</span><strong>Café Shop</strong><small>${player!.tokens} tokens · ${unownedDecorations ? `${unownedDecorations} to collect` : 'collection complete'}</small></button><button class="tile-button" data-action="customize"><span>🖌️</span><strong>Customize Café</strong><small>${showingDecorations} showing · ${ownedDecorations} owned</small></button><button class="tile-button" data-action="stickers"><span>✦</span><strong>Sticker Book</strong><small>${player!.cats.length}/${CATS.length} cats · ${player!.badges.length}/${BADGES.length} badges</small></button></div>
  </section></main>`;
}

function newRound(): void {
  if (!player) return;
  processing = false;
  const selected = [...player.settings.operations];
  const round: RoundState = { id: makeId('round'), status: 'active', index: 0, current: null, currentInputMode: player.settings.inputMode, currentFirstAttempted: false, currentFirstCorrect: false, currentHelped: false, wrongAttempts: 0, results: [], operationPlan: buildOperationPlan(selected), reviewQueue: [], seenQuestionIds: [], selectedOperations: selected, rewardCommitted: false, newBadges: [] };
  player.round = round;
  prepareCurrent();
  screen = 'round'; feedback = 'none'; keypadValue = ''; safeSave(); render();
}

function prepareCurrent(): void {
  if (!player?.round || player.round.current) return;
  const round = player.round;
  const plannedOperation = round.operationPlan[round.index] ?? player.settings.operations[0]!;
  const dueIndex = round.reviewQueue.findIndex((item) => item.due <= round.index && item.question.operation === plannedOperation);
  let question: Question;
  if (dueIndex >= 0) {
    const queued = round.reviewQueue.splice(dueIndex, 1)[0]!;
    question = avoidRepeatedMenuFood({ ...queued.question, id: makeId('q') }, player.lastMenuFood);
  } else {
    const operation = plannedOperation;
    const puzzle = player.settings.puzzles && round.index === 6;
    question = generateQuestion(operation, player.settings, player.factStats, round.seenQuestionIds, puzzle, Math.random, player.lastMenuFood);
  }
  round.current = question;
  player.lastMenuFood = question.food;
  round.currentInputMode = player.settings.inputMode;
  round.seenQuestionIds.push(question.factKey);
  round.currentFirstAttempted = false; round.currentFirstCorrect = false; round.currentHelped = false; round.wrongAttempts = 0;
}

function roundHtml(): string {
  const round = player!.round!;
  const q = round.current!;
  const customers = cafeCustomerCats(player!);
  const customer = customers[round.index % customers.length]!;
  const progress = Array.from({ length: 10 }, (_, index) => `<span class="paw ${index < round.index ? 'done' : index === round.index ? 'current' : ''}">${index < round.index ? '●' : '○'}</span>`).join('');
  const choices = round.currentInputMode === 'choices' ? `<div class="answer-choices">${choiceAnswers(q, seededRng(q.id)).map((answer) => `<button class="answer-button" data-answer="${answer}">${answer}</button>`).join('')}</div>` : `<div class="keypad"><output aria-live="polite">${keypadValue || '—'}</output><div>${[7,8,9,4,5,6,1,2,3].map((n) => `<button data-key="${n}">${n}</button>`).join('')}<button data-key="back" aria-label="Backspace">⌫</button><button data-key="0">0</button><button class="serve-key" data-action="serve">Serve</button></div></div>`;
  const help = feedback === 'wrong' || feedback === 'help' ? `<aside class="help-card"><div>${mathVisual(q)}</div><div><strong>${feedback === 'wrong' ? 'Let’s look together.' : 'Here’s a café clue.'}</strong><p>${explanationFor(q)}</p>${round.wrongAttempts >= 2 ? `<button class="primary" data-answer="${q.answer}">Serve ${q.answer} together</button>` : '<p class="retry-note">Try that same order once more.</p>'}</div></aside>` : '';
  return `<main class="game"><section class="game-stage ${feedback === 'serving' ? 'is-serving' : ''} ${cafeThemeClass()}">
    <div class="round-bar"><button class="text-button" data-action="close-round">← Close café</button><div class="round-progress" aria-label="Round progress">${progress}</div><span>${round.index + 1} / 10</span></div>
    <div class="cafe-scene"><div class="scene-window"><span class="moon">✦</span></div>${sceneDecorationsHtml()}<button class="customer-cat" data-action="pet-cat" aria-label="Gently tap ${customer.name}">${catSvg(customer.id, feedback === 'serving' ? 'happy' : 'waiting', player!.equipped.wearable ?? '')}</button><div class="order-bubble"><p>${highlightedStoryHtml(q)}</p><div class="equation ${equationWidthClass(q, feedback === 'serving')}" aria-live="polite">${equationHtml(q, feedback === 'serving')}</div>${q.presentation === 'missing' ? '<span class="puzzle-label">Puzzle order</span>' : ''}</div></div>
    <div class="answer-area">${choices}<button class="show-me" data-action="help">Show me</button></div>
    ${help}<div class="countertop"><div class="plate">${servingPlateSvg(q.food, q.answer, feedback === 'serving')}</div><div class="mug">☕</div>${counterDecorationHtml(player!.equipped.counter)}</div>
  </section></main>`;
}

function summaryHtml(): string {
  const round = player!.round!;
  const independent = round.results.filter((result) => result.firstCorrect && !result.helped).length;
  const helped = round.results.filter((result) => result.helped).length;
  const cat = CATS.find((item) => item.id === round.newCatId);
  return `<main class="summary"><section class="summary-card">
    <p class="eyebrow">Café closed — all cats happy</p><h1>Lovely serving!</h1><div class="summary-numbers"><div><strong>${round.results.length}</strong><span>orders served</span></div><div><strong>${independent}</strong><span>first-try answers</span></div><div><strong>${helped}</strong><span>clues used</span></div></div>
    <button class="token-reward" data-action="shop"><span aria-hidden="true">🪙</span><div><strong>+${round.tokensEarned ?? 0} Café Tokens</strong><small>${player!.tokens} tokens ready · Open Café Shop →</small></div></button>
    ${cat ? `<div class="collectible-reveal"><div>${catSvg(cat.id, 'happy')}</div><div><span>New cat sticker</span><h2>${cat.name}</h2><p>Found a cozy spot in your Sticker Book.</p></div></div>` : `<div class="collection-complete"><strong>Your cat collection is complete!</strong><p>The café is always open for another round.</p></div>`}
    ${round.newBadges.length ? `<div class="badge-reveals"><h2>Badge shelf</h2>${round.newBadges.map((id) => `<span>${badgeIconHtml(id)}${badgeById(id)?.name ?? 'New badge'}</span>`).join('')}</div>` : ''}
    <div class="summary-actions"><button class="primary jumbo" data-action="another-round">Another round</button><button class="secondary jumbo" data-action="finish-round">Close the café</button></div>
  </section></main>`;
}

function completeRound(): void {
  if (!player?.round) return;
  const round = player.round;
  round.status = 'complete'; round.current = null;
  commitRoundRewards(player, round);
  safeSave(); audio.play('reward'); screen = 'summary'; feedback = 'none'; render();
}

function firstAttempt(answer: number): boolean {
  const round = player!.round!;
  const q = round.current!;
  const correct = answer === q.answer;
  const key = factStatKey(q, round.currentInputMode);
  const stat = player!.factStats[key] ?? { firstCorrect: 0, firstMisses: 0, helpUses: 0, independent: 0, recent: [] };
  const hadPriorMiss = Object.entries(player!.factStats).some(([candidate, value]) => candidate.startsWith(`${q.factKey}|`) && value.firstMisses > 0);
  stat.recent = [...stat.recent.slice(-4), correct && !round.currentHelped];
  if (correct && !round.currentHelped) {
    stat.firstCorrect += 1; stat.independent += 1;
    player!.totals.independent += 1; player!.totals.byOperation[q.operation] += 1;
    recordIndependentFact(player!, q);
    if (hadPriorMiss && !player!.totals.recoveredFacts.includes(q.factKey)) player!.totals.recoveredFacts.push(q.factKey);
  } else {
    stat.firstMisses += correct ? 0 : 1;
  }
  player!.factStats[key] = stat;
  round.currentFirstAttempted = true;
  round.currentFirstCorrect = correct && !round.currentHelped;
  if (!correct) {
    round.currentHelped = true; stat.helpUses += 1;
    if (!round.reviewQueue.some((item) => item.question.factKey === q.factKey)) round.reviewQueue.push({ due: round.index + 3, question: q });
  }
  return correct;
}

async function submitAnswer(answer: number): Promise<void> {
  if (processing || !player?.round?.current || !Number.isFinite(answer)) return;
  processing = true;
  const round = player.round;
  const q: Question = round.current!;
  let correct: boolean;
  if (!round.currentFirstAttempted) correct = firstAttempt(answer);
  else correct = answer === q.answer;
  if (!correct) {
    round.wrongAttempts += 1;
    round.currentHelped = true; feedback = 'wrong'; keypadValue = ''; safeSave(); processing = false; render(); return;
  }
  round.results.push({ questionId: q.id, factKey: q.factKey, operation: q.operation, presentation: q.presentation, inputMode: round.currentInputMode, firstCorrect: round.currentFirstCorrect, helped: round.currentHelped });
  const newBadges = unlockBadges(player);
  round.newBadges.push(...newBadges.filter((id) => !round.newBadges.includes(id)));
  newBadges.forEach((id) => showToast(`New badge: ${badgeById(id)?.name}`));
  feedback = 'serving'; audio.play('serve'); safeSave(); render();
  await new Promise((resolve) => window.setTimeout(resolve, 800));
  round.index += 1; round.current = null; keypadValue = ''; feedback = 'none';
  processing = false;
  if (round.index >= 10) completeRound();
  else { prepareCurrent(); safeSave(); render(); }
}

function useHelp(): void {
  const round = player?.round;
  if (!round?.current || round.currentHelped) { feedback = 'help'; render(); return; }
  round.currentHelped = true; feedback = 'help';
  const key = factStatKey(round.current, round.currentInputMode);
  const stat = player!.factStats[key] ?? { firstCorrect: 0, firstMisses: 0, helpUses: 0, independent: 0, recent: [] };
  stat.helpUses += 1; player!.factStats[key] = stat; safeSave(); render();
}

function wireCommon(): void {
  document.querySelectorAll<HTMLElement>('[data-action]').forEach((button) => button.addEventListener('click', () => {
    const action = button.dataset.action;
    if (action === 'settings') openModal('settings', action);
    else if (action === 'stickers') { stickerPage = 0; collectionTab = 'cats'; openModal('stickers', action); }
    else if (action === 'shop') { shopFilter = 'all'; openModal('shop', action); }
    else if (action === 'customize') {
      customizeSlot = DECORATION_SLOTS.find((slot) => player!.equipped[slot] || DECORATIONS.some((item) => item.slot === slot && player!.cosmetics.includes(item.id))) ?? 'wearable';
      openModal('customize', action);
    }
    else if (action === 'players') openModal('players', action);
    else if (action === 'grownups') openModal('grownups', action);
    else if (action === 'lobby') { screen = 'lobby'; render(); }
    else if (action === 'new-round' || action === 'another-round') newRound();
    else if (action === 'continue') { screen = 'round'; prepareCurrent(); render(); }
    else if (action === 'close-round') { screen = 'lobby'; safeSave(); render(); }
    else if (action === 'finish-round') { player!.round = null; screen = 'lobby'; safeSave(); render(); }
    else if (action === 'help') useHelp();
    else if (action === 'sound') void toggleSound();
    else if (action === 'pet-cat') discover('purr-service', 'Purr service! A secret badge appeared.');
    else if (action === 'sleepy') discover('not-a-croissant', 'Not a croissant! You found the sleepy cat.');
  }));
  document.querySelector<HTMLInputElement>('[data-header-volume]')?.addEventListener('input', (event) => {
    player!.settings.volume = Number((event.currentTarget as HTMLInputElement).value);
    audio.setVolume(player!.settings.volume); safeSave();
  });
}

async function toggleSound(): Promise<void> {
  if (!player) return;
  if (!audio.enabled) {
    const ok = await audio.enable(player.settings.volume);
    if (ok) { player.settings.soundPreferred = true; safeSave(); showToast('Sound is on'); }
    else showToast('This browser kept sound off');
  } else {
    const muted = audio.toggleMute();
    player.settings.soundPreferred = !muted;
    safeSave();
    showToast(muted ? 'Sound is off' : 'Sound is on');
  }
  render();
}

function discover(id: string, message: string): void {
  if (!player || player.badges.includes(id)) { audio.play('cat'); return; }
  player.badges.push(id); safeSave(); audio.play('cat'); showToast(message); render();
}

function wireRound(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-answer]').forEach((button) => button.addEventListener('click', () => void submitAnswer(Number(button.dataset.answer))));
  document.querySelectorAll<HTMLButtonElement>('[data-key]').forEach((button) => button.addEventListener('click', () => {
    const key = button.dataset.key!;
    keypadValue = key === 'back' ? keypadValue.slice(0, -1) : (keypadValue + key).slice(0, 3);
    render();
  }));
  document.querySelector<HTMLElement>('[data-action="serve"]')?.addEventListener('click', () => { if (keypadValue) void submitAnswer(Number(keypadValue)); });
}

window.addEventListener('keydown', (event) => {
  if (screen !== 'round' || modal || player?.round?.currentInputMode !== 'keypad') return;
  if (/^\d$/.test(event.key)) { keypadValue = (keypadValue + event.key).slice(0, 3); render(); }
  else if (event.key === 'Backspace') { keypadValue = keypadValue.slice(0, -1); render(); }
  else if (event.key === 'Enter' && keypadValue) void submitAnswer(Number(keypadValue));
});

function modalHtml(kind: NonNullable<typeof modal>): string {
  if (kind === 'settings') return settingsModal();
  if (kind === 'stickers') return stickersModal();
  if (kind === 'shop') return shopModal();
  if (kind === 'customize') return customizeModal();
  if (kind === 'grownups') return grownupsModal();
  return playersModal();
}

function modalShell(title: string, body: string, className = ''): string {
  return `<div class="modal-backdrop"><section class="modal ${className}" role="dialog" aria-modal="true" aria-labelledby="modal-title"><header><h2 id="modal-title">${title}</h2><button class="icon-button" data-close-modal aria-label="Close">×</button></header>${body}</section></div>`;
}

function settingsModal(): string {
  const s = player!.settings;
  const opButtons = (Object.keys(OP_INFO) as Operation[]).map((op) => `<label class="operation-toggle"><input type="checkbox" name="operation" value="${op}" ${s.operations.includes(op) ? 'checked' : ''}/><strong>${OP_INFO[op].symbol}</strong><span>${OP_INFO[op].word}</span></label>`).join('');
  const numberChecks = (name: string, selected: number[]) => Array.from({ length: 12 }, (_, i) => i + 1).map((n) => `<label class="number-check"><input type="checkbox" name="${name}" value="${n}" ${selected.includes(n) ? 'checked' : ''}/><span>${n}</span></label>`).join('');
  return modalShell('Café settings', `<form id="settings-form"><p class="setting-note">Changes apply to the next customer. Pick at least one operation.</p><fieldset><legend>What shall we practice?</legend><div class="operation-grid">${opButtons}</div></fieldset>
    <div class="settings-columns"><fieldset><legend>Addition & subtraction</legend><label>Addition range<select name="addLimit">${[5,10,20].map((n) => `<option ${s.addLimit === n ? 'selected' : ''} value="${n}">Within ${n}</option>`).join('')}</select></label><label>Subtraction range<select name="subtractLimit">${[5,10,20].map((n) => `<option ${s.subtractLimit === n ? 'selected' : ''} value="${n}">Within ${n}</option>`).join('')}</select></label></fieldset>
    <fieldset><legend>Multiplication tables</legend><div class="number-grid">${numberChecks('multiply-table', s.multiplicationTables)}</div></fieldset><fieldset><legend>Division families</legend><div class="number-grid">${numberChecks('divide-family', s.divisionFamilies)}</div></fieldset></div>
    <fieldset><legend>Answers</legend><div class="segmented"><label><input type="radio" name="inputMode" value="choices" ${s.inputMode === 'choices' ? 'checked' : ''}/><span>Tap choices</span></label><label><input type="radio" name="inputMode" value="keypad" ${s.inputMode === 'keypad' ? 'checked' : ''}/><span>Number pad</span></label></div><label class="inline-check"><input type="checkbox" name="extend" ${s.factorMax === 12 ? 'checked' : ''}/> Extend the other factor through 12</label><label class="inline-check"><input type="checkbox" name="puzzles" ${s.puzzles ? 'checked' : ''}/> Puzzle orders (about one each round)</label></fieldset>
    <fieldset><legend>Sound preference</legend><p class="setting-note">Your sound choice stays saved on this device.</p><label>Volume <input type="range" name="volume" min="0" max="1" step="0.05" value="${s.volume}"/></label></fieldset>
    <footer><button type="button" class="text-button" data-open-grownups>Grown-ups</button><button class="primary" type="submit">Save settings</button></footer></form>`);
}

function stickersModal(): string {
  const tabs = `<div class="collection-tabs" role="tablist" aria-label="Sticker Book sections"><button role="tab" id="collection-tab-cats" aria-controls="collection-panel" aria-selected="${collectionTab === 'cats'}" tabindex="${collectionTab === 'cats' ? '0' : '-1'}" data-collection-tab="cats">Cats <small>${player!.cats.length}/${CATS.length}</small></button><button role="tab" id="collection-tab-badges" aria-controls="collection-panel" aria-selected="${collectionTab === 'badges'}" tabindex="${collectionTab === 'badges' ? '0' : '-1'}" data-collection-tab="badges">Badges <small>${player!.badges.length}/${BADGES.length}</small></button></div>`;
  if (collectionTab === 'badges') {
    const badges = BADGES.map((badge) => {
      const unlocked = player!.badges.includes(badge.id);
      const progress = badge.progress?.(player!);
      return `<article class="badge-card ${unlocked ? 'unlocked' : ''}">${badgeIconHtml(badge.id, Boolean(badge.secret && !unlocked))}<div><strong>${unlocked || !badge.secret ? badge.name : 'Secret badge'}</strong><p>${unlocked || !badge.secret ? badge.description : 'A playful clue is hiding in the café.'}</p>${progress && !unlocked ? `<progress aria-label="${badge.name} progress" max="${progress[1]}" value="${Math.min(progress[0], progress[1])}"></progress><small>${progress[0]} / ${progress[1]}</small>` : ''}</div></article>`;
    }).join('');
    return modalShell('Sticker Book', `<div class="stickerbook">${tabs}<section id="collection-panel" role="tabpanel" aria-labelledby="collection-tab-badges"><div class="collection-heading"><h3>Badge shelf</h3><p>Longer café adventures and secret discoveries live here.</p></div><div class="badge-grid">${badges}</div></section></div>`, 'wide collection-modal');
  }
  const pages = catBookPages();
  stickerPage = Math.max(0, Math.min(stickerPage, pages.length - 1));
  const catSlides = pages.map((page, pageIndex) => `<div class="sticker-grid" data-cat-slide="${pageIndex}" aria-hidden="${pageIndex !== stickerPage}">${page.map((cat) => {
    if (!cat) return '<div class="sticker locked"><div>?</div><strong>Mystery cat</strong></div>';
    const unlocked = player!.cats.includes(cat.id);
    return `<div class="sticker ${unlocked ? '' : 'locked'}"><div>${unlocked ? catSvg(cat.id, 'happy') : '?'}</div><strong>${unlocked ? cat.name : 'Mystery cat'}</strong></div>`;
  }).join('')}</div>`).join('');
  const catPager = `<div class="cat-book-heading"><div><h3>Cat collection</h3><p>${player!.cats.length} of ${CATS.length} found</p></div><span data-cat-page-status aria-live="polite">Page ${stickerPage + 1} of ${pages.length}</span></div><div class="cat-slider" data-cat-slider><div class="cat-slider-track" data-cat-slider-track>${catSlides}</div></div><nav class="cat-book-pager" aria-label="Cat collection pages"><button class="soft" data-cat-step="-1" ${stickerPage === 0 ? 'disabled' : ''}>← Previous</button><strong data-cat-page-status aria-hidden="true">Page ${stickerPage + 1} of ${pages.length}</strong><button class="soft" data-cat-step="1" ${stickerPage === pages.length - 1 ? 'disabled' : ''}>Next →</button></nav>`;
  return modalShell('Sticker Book', `<div class="stickerbook">${tabs}<section id="collection-panel" role="tabpanel" aria-labelledby="collection-tab-cats">${catPager}</section></div>`, 'wide collection-modal');
}

function decorationPreviewHtml(item: DecorationDefinition): string {
  if (item.slot === 'wearable') return `<div class="shop-cat-preview" aria-hidden="true">${catSvg(player!.avatar, 'happy', item.id)}</div>`;
  if (item.slot === 'theme') return `<div class="theme-swatch theme-${item.id.replace('-theme', '')}" aria-hidden="true"><i></i><i></i><i></i></div>`;
  if (item.slot === 'counter') return `<div class="shop-item-icon shop-counter-preview" aria-hidden="true">${counterDecorationHtml(item.id)}</div>`;
  if (item.slot === 'wall') {
    const wallPreviews: Record<string, string> = { 'number-poster': '2 · 4 · 6 · 8', 'paw-poster': '● + ● = ♥', 'bakery-menu': 'MENU<br>☕ · 🥐 ·', 'times-chalkboard': '3 × 4 = 12', 'garden-print': '🌿 ✿ 🌱', 'cat-portrait': '🐱' };
    return `<div class="shop-scene-preview" aria-hidden="true"><div class="shop-wall-mini wall-${item.id}">${wallPreviews[item.id] ?? item.icon}</div></div>`;
  }
  if (item.slot === 'room') {
    const roomPreviews: Record<string, string> = { bunting: '<span class="mini-bunting">▲ ● ▲ ● ▲</span>', 'cafe-curtains': '<span class="mini-curtain"></span><span class="mini-curtain"></span>', 'hanging-plants': '<span class="mini-plants">🪴　🪴</span>', 'string-lights': '<span class="mini-lights">● · ● · ● · ●</span>', 'paper-lanterns': '<span class="mini-lanterns">●　●　●　●</span>' };
    return `<div class="shop-scene-preview room-preview room-${item.id}" aria-hidden="true">${roomPreviews[item.id] ?? item.icon}</div>`;
  }
  return `<div class="shop-item-icon" aria-hidden="true">${item.icon}</div>`;
}

function shopPurchaseControl(item: DecorationDefinition): { label: string; ariaLabel: string; disabled: boolean; state: string } {
  if (player!.tokens < item.price) return { label: `Need ${item.price - player!.tokens} more`, ariaLabel: `Need ${item.price - player!.tokens} more Café Tokens for ${item.name}`, disabled: true, state: 'locked' };
  return { label: 'Buy', ariaLabel: `Buy ${item.name} for ${item.price} Café Tokens`, disabled: false, state: 'available' };
}

function shopModal(): string {
  const unowned = DECORATIONS.filter((item) => !player!.cosmetics.includes(item.id));
  if (shopFilter !== 'all' && !unowned.some((item) => item.slot === shopFilter)) shopFilter = 'all';
  const filters: Array<'all' | DecorationSlot> = ['all', ...DECORATION_SLOTS.filter((slot) => unowned.some((item) => item.slot === slot))];
  const filterButtons = filters.map((filter) => `<button type="button" data-shop-filter="${filter}" class="${shopFilter === filter ? 'selected' : ''}" aria-pressed="${shopFilter === filter}">${filter === 'all' ? 'All' : DECORATION_SLOT_NAMES[filter]}</button>`).join('');
  const cards = unowned.map((item) => {
    const control = shopPurchaseControl(item);
    const hidden = shopFilter !== 'all' && shopFilter !== item.slot;
    return `<article class="shop-card ${hidden ? 'shop-hidden' : ''}" data-shop-card="${item.id}" data-shop-slot="${item.slot}">${decorationPreviewHtml(item)}<div class="shop-card-copy"><h3>${item.name}</h3><p>${item.description}</p><span data-shop-status>🪙 ${item.price} Café Tokens</span></div><button type="button" class="shop-action ${control.state}" data-shop-item="${item.id}" aria-label="${control.ariaLabel}" ${control.disabled ? 'disabled' : ''}>${control.label}</button></article>`;
  }).join('');
  const empty = unowned.length ? '' : 'You own everything in the Café Shop. Your collection is ready in Customize Café.';
  return modalShell(`Café Shop <small class="shop-title-balance">🪙 <span data-token-balance>${player!.tokens}</span></small>`, `<div class="cafe-shop"><section class="shop-intro"><div class="token-balance" aria-label="Café Token balance"><span aria-hidden="true">🪙</span><strong data-token-balance>${player!.tokens}</strong><small>Café Tokens</small></div><p>Earn ${ROUND_TOKEN_REWARD} Café Tokens whenever you finish a round. The Shop only shows items you have not collected yet.</p></section>${unowned.length ? `<nav class="shop-filters" aria-label="Shop categories">${filterButtons}</nav>` : ''}<p class="shop-live" aria-live="polite" data-shop-live></p><p class="shop-empty" data-shop-empty ${empty ? '' : 'hidden'}>${empty}</p><div class="shop-grid">${cards}</div></div>`, 'wide shop-modal');
}

function customizeModal(): string {
  const owned = DECORATIONS.filter((item) => player!.cosmetics.includes(item.id));
  const ownedSlots = DECORATION_SLOTS.filter((slot) => owned.some((item) => item.slot === slot));
  if (!ownedSlots.includes(customizeSlot)) customizeSlot = ownedSlots[0] ?? 'wearable';
  const filterButtons = ownedSlots.map((slot) => `<button type="button" data-customize-filter="${slot}" class="${customizeSlot === slot ? 'selected' : ''}" aria-pressed="${customizeSlot === slot}">${DECORATION_SLOT_NAMES[slot]}</button>`).join('');
  const cards = owned.map((item) => {
    const showing = player!.equipped[item.slot] === item.id;
    const hidden = item.slot !== customizeSlot;
    return `<article class="shop-card customize-card owned ${showing ? 'showing' : ''} ${hidden ? 'shop-hidden' : ''}" data-customize-card="${item.id}" data-customize-slot="${item.slot}">${decorationPreviewHtml(item)}<div class="shop-card-copy"><h3>${item.name}</h3><p>${item.description}</p><span data-customize-status>${showing ? 'Showing in your café' : 'In your collection'}</span></div><button type="button" class="shop-action ${showing ? 'showing' : 'owned'}" data-customize-item="${item.id}" aria-label="${showing ? `Put away ${item.name}` : `Show ${item.name}`}">${showing ? 'Put away' : 'Show'}</button></article>`;
  }).join('');
  const current = decorationById(player!.equipped[customizeSlot]);
  const body = owned.length
    ? `<div class="cafe-customizer"><section class="customize-intro"><div><small>Showing in this area</small><strong data-customize-current>${current?.slot === customizeSlot ? current.name : 'Nothing showing'}</strong></div><p>Choose one item from each section to show in your café. You can also leave any section empty.</p></section><nav class="shop-filters customize-filters" aria-label="Café areas">${filterButtons}</nav><p class="shop-live" aria-live="polite" data-customize-live></p><div class="shop-grid customize-grid">${cards}</div></div>`
    : '<div class="customize-empty"><span aria-hidden="true">🖌️</span><h3>Your decorating shelf is empty</h3><p>Collect something from the Café Shop, then come back here to decide what to show.</p></div>';
  return modalShell('Customize Café', body, 'wide customize-modal');
}

function playersModal(): string {
  const players = listPlayers();
  const avatarIds = [...new Set([...AVATARS, ...player!.cats])].filter((id) => CATS.some((cat) => cat.id === id));
  return modalShell('Players', `<div class="player-list">${players.map((saved) => `<button class="player-card ${saved.id === player!.id ? 'active' : ''}" data-switch-player="${saved.id}"><span ${saved.id === player!.id ? 'data-current-player-avatar' : ''}>${catSvg(saved.avatar)}</span><strong data-saved-name="${saved.id}"></strong><small>${saved.id === player!.id ? 'Playing now' : `${saved.totals.rounds} rounds`}</small></button>`).join('')}</div><button class="secondary full" data-new-player>+ Add a player</button><form id="rename-form"><label class="field">Change this player’s name<input id="rename-input" maxlength="24" required /></label><fieldset><legend>Change café cat</legend><div class="profile-editor-preview" data-profile-avatar-preview>${catSvg(player!.avatar, 'happy')}</div><div class="avatar-grid compact">${avatarIds.map((id) => `<label class="avatar-choice"><input type="radio" name="profile-avatar" value="${id}" ${player!.avatar === id ? 'checked' : ''}/><span>${catSvg(id)}</span></label>`).join('')}</div></fieldset><button class="primary" type="submit">Save player</button></form>`);
}

function grownupsModal(): string {
  const entries = Object.entries(player!.factStats);
  const correct = entries.reduce((sum, [, stat]) => sum + stat.firstCorrect, 0);
  const misses = entries.reduce((sum, [, stat]) => sum + stat.firstMisses, 0);
  const help = entries.reduce((sum, [, stat]) => sum + stat.helpUses, 0);
  const modes = (['choices', 'keypad'] as InputMode[]).map((mode) => {
    const subset = entries.filter(([key]) => key.endsWith(`|${mode}`));
    const hits = subset.reduce((sum, [, stat]) => sum + stat.firstCorrect, 0);
    const attempts = hits + subset.reduce((sum, [, stat]) => sum + stat.firstMisses, 0);
    return `<li><strong>${mode === 'choices' ? 'Tap choices' : 'Number pad'}:</strong> ${hits} / ${attempts} first-try</li>`;
  }).join('');
  const revisit = entries.filter(([, stat]) => stat.recent.slice(-3).filter((x) => !x).length >= 2).slice(0, 5).map(([key]) => key.split('|')[0]!.replaceAll(':', ' · '));
  return modalShell('Grown-ups', `<div class="grownups"><p>Progress lives in this browser on this device. Clearing site data can remove it, and it does not automatically sync between tablets. Export and import let you move a player safely.</p><h3>Practice snapshot</h3><p><strong>${correct} / ${correct + misses}</strong> first-try answers · <strong>${help}</strong> clues used</p><ul>${modes}</ul><p><strong>Worth revisiting:</strong> ${revisit.length ? revisit.join(', ') : 'Nothing stands out yet.'}</p><h3>Backup & restore</h3><div class="backup-actions"><button class="secondary" data-export>Export player save</button><label class="secondary file-button">Import player save<input type="file" accept="application/json" data-import/></label></div><h3>Danger zone</h3><button class="danger" data-delete-player>Delete this player…</button></div>`);
}

function selectCollectionTab(tab: 'cats' | 'badges'): void {
  collectionTab = tab;
  render();
  window.requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(`[data-collection-tab="${tab}"]`)?.focus());
}

function wireModal(): void {
  document.querySelectorAll<HTMLElement>('#app > :not(.modal-backdrop)').forEach((node) => { node.inert = true; });
  const dialog = document.querySelector<HTMLElement>('.modal');
  const closeButton = document.querySelector<HTMLButtonElement>('[data-close-modal]');
  closeButton?.addEventListener('click', closeModal);
  document.querySelector('.modal-backdrop')?.addEventListener('click', (event) => { if (event.target === event.currentTarget) closeModal(); });
  dialog?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { event.preventDefault(); closeModal(); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')].filter((node) => node.getClientRects().length > 0);
    const first = focusable[0]; const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  if (focusNewModal) { focusNewModal = false; window.requestAnimationFrame(() => closeButton?.focus()); }
  const form = document.querySelector<HTMLFormElement>('#settings-form');
  form?.addEventListener('submit', (event) => {
    event.preventDefault(); const data = new FormData(form);
    const operations = data.getAll('operation') as Operation[];
    if (!operations.length) { showToast('Keep at least one operation on'); return; }
    player!.settings.operations = operations;
    player!.settings.addLimit = Number(data.get('addLimit')) as 5 | 10 | 20;
    player!.settings.subtractLimit = Number(data.get('subtractLimit')) as 5 | 10 | 20;
    player!.settings.multiplicationTables = data.getAll('multiply-table').map(Number); if (!player!.settings.multiplicationTables.length) player!.settings.multiplicationTables = [2];
    player!.settings.divisionFamilies = data.getAll('divide-family').map(Number); if (!player!.settings.divisionFamilies.length) player!.settings.divisionFamilies = [2];
    player!.settings.factorMax = data.has('extend') ? 12 : 10; player!.settings.puzzles = data.has('puzzles'); player!.settings.inputMode = data.get('inputMode') as InputMode;
    player!.settings.volume = Number(data.get('volume')); audio.setVolume(player!.settings.volume);
    if (player!.round?.status === 'active' && player!.round.current) {
      const start = player!.round.index + 1;
      const remaining = 10 - start;
      player!.round.operationPlan.splice(start, remaining, ...buildOperationPlan(operations, remaining));
      player!.round.selectedOperations = [...operations];
    }
    safeSave(); showToast('Settings saved for the next customer'); closeModal();
  });
  document.querySelector('[data-open-grownups]')?.addEventListener('click', () => openModal('grownups'));
  const rename = document.querySelector<HTMLFormElement>('#rename-form');
  const renameInput = document.querySelector<HTMLInputElement>('#rename-input');
  if (renameInput) renameInput.value = player!.name;
  document.querySelectorAll<HTMLInputElement>('input[name="profile-avatar"]').forEach((input) => input.addEventListener('change', () => {
    if (!input.checked) return;
    const preview = catSvg(input.value, 'happy');
    const editorPreview = document.querySelector<HTMLElement>('[data-profile-avatar-preview]');
    const currentPlayerPreview = document.querySelector<HTMLElement>('[data-current-player-avatar]');
    const brandPreview = document.querySelector<HTMLElement>('[data-brand-avatar]');
    if (editorPreview) editorPreview.innerHTML = preview;
    if (currentPlayerPreview) currentPlayerPreview.innerHTML = preview;
    if (brandPreview) brandPreview.innerHTML = preview;
  }));
  rename?.addEventListener('submit', (event) => { event.preventDefault(); const data = new FormData(rename); player!.name = renameInput!.value.trim().slice(0, 24) || player!.name; player!.avatar = String(data.get('profile-avatar') ?? player!.avatar); safeSave(); showToast('Player saved'); closeModal(); });
  for (const saved of listPlayers()) {
    const label = document.querySelector<HTMLElement>(`[data-saved-name="${saved.id}"]`);
    if (label) label.textContent = saved.name;
  }
  document.querySelectorAll<HTMLElement>('[data-switch-player]').forEach((button) => button.addEventListener('click', () => switchPlayer(button.dataset.switchPlayer!)));
  document.querySelector('[data-new-player]')?.addEventListener('click', () => { player = null; modal = null; renderOnboarding(); });
  document.querySelectorAll<HTMLButtonElement>('[data-collection-tab]').forEach((button) => {
    button.addEventListener('click', () => selectCollectionTab(button.dataset.collectionTab as 'cats' | 'badges'));
    button.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      selectCollectionTab(collectionTab === 'cats' ? 'badges' : 'cats');
    });
  });
  document.querySelectorAll<HTMLElement>('[data-cat-page]').forEach((button) => button.addEventListener('click', () => showCatPage(Number(button.dataset.catPage))));
  document.querySelectorAll<HTMLElement>('[data-cat-step]').forEach((button) => button.addEventListener('click', () => showCatPage(stickerPage + Number(button.dataset.catStep))));
  const catSlider = document.querySelector<HTMLElement>('[data-cat-slider]');
  const catTrack = document.querySelector<HTMLElement>('[data-cat-slider-track]');
  if (catTrack) showCatPage(stickerPage);
  let dragStart: { pointerId: number; pointerType: string; x: number; y: number; time: number; axis: 'pending' | 'horizontal' | 'vertical' } | null = null;
  let suppressSliderClick = false;
  let suppressClickTimer: number | undefined;
  catSlider?.addEventListener('pointerdown', (event) => {
    if (dragStart || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    dragStart = { pointerId: event.pointerId, pointerType: event.pointerType, x: event.clientX, y: event.clientY, time: performance.now(), axis: 'pending' };
  });
  catSlider?.addEventListener('pointermove', (event) => {
    if (!dragStart || event.pointerId !== dragStart.pointerId) return;
    let horizontal = event.clientX - dragStart.x;
    const vertical = Math.abs(event.clientY - dragStart.y);
    if (dragStart.axis === 'pending') {
      if (Math.max(Math.abs(horizontal), vertical) < 8) return;
      dragStart.axis = Math.abs(horizontal) > vertical ? 'horizontal' : 'vertical';
      if (dragStart.axis === 'horizontal') {
        catSlider.setPointerCapture(event.pointerId);
        catTrack?.classList.add('dragging');
        catSlider.classList.add('dragging');
      }
    }
    if (dragStart.axis !== 'horizontal') return;
    if (!catSlider.hasPointerCapture(event.pointerId)) {
      catSlider.setPointerCapture(event.pointerId);
      catTrack?.classList.add('dragging');
      catSlider.classList.add('dragging');
    }
    event.preventDefault();
    const lastPage = catBookPages().length - 1;
    if ((stickerPage === 0 && horizontal > 0) || (stickerPage === lastPage && horizontal < 0)) horizontal *= .28;
    if (catTrack) catTrack.style.transform = `translate3d(calc(${-stickerPage * 100}% + ${horizontal}px),0,0)`;
  });
  catSlider?.addEventListener('pointerup', (event) => {
    if (!dragStart || event.pointerId !== dragStart.pointerId) return;
    const horizontal = event.clientX - dragStart.x;
    const elapsed = Math.max(1, performance.now() - dragStart.time);
    const wasDragging = dragStart.axis === 'horizontal';
    dragStart = null;
    catSlider.classList.remove('dragging');
    if (!wasDragging) return;
    suppressSliderClick = true;
    window.clearTimeout(suppressClickTimer);
    suppressClickTimer = window.setTimeout(() => { suppressSliderClick = false; }, 0);
    catTrack?.classList.remove('dragging');
    if (catTrack) void catTrack.offsetWidth;
    const farEnough = Math.abs(horizontal) >= Math.min(80, catSlider.clientWidth * .18);
    const fastEnough = Math.abs(horizontal) / elapsed > .55;
    showCatPage(stickerPage + (farEnough || fastEnough ? (horizontal < 0 ? 1 : -1) : 0));
  });
  catSlider?.addEventListener('pointercancel', (event) => {
    if (!dragStart || event.pointerId !== dragStart.pointerId) return;
    const wasDragging = dragStart.axis === 'horizontal';
    dragStart = null;
    catSlider.classList.remove('dragging');
    catTrack?.classList.remove('dragging');
    if (wasDragging) {
      if (catTrack) void catTrack.offsetWidth;
      showCatPage(stickerPage);
    }
  });
  catSlider?.addEventListener('lostpointercapture', (event) => {
    if (event.target !== catSlider || !dragStart || event.pointerId !== dragStart.pointerId || dragStart.axis !== 'horizontal') return;
    dragStart = null;
    catSlider.classList.remove('dragging');
    catTrack?.classList.remove('dragging');
    if (catTrack) void catTrack.offsetWidth;
    showCatPage(stickerPage);
  });
  catSlider?.addEventListener('pointerleave', (event) => {
    if (!dragStart || event.pointerId !== dragStart.pointerId || dragStart.pointerType !== 'mouse' || dragStart.axis === 'horizontal') return;
    dragStart = null;
  });
  catSlider?.addEventListener('click', (event) => {
    if (!suppressSliderClick) return;
    event.preventDefault();
    event.stopPropagation();
    suppressSliderClick = false;
    window.clearTimeout(suppressClickTimer);
  }, true);
  document.querySelectorAll<HTMLButtonElement>('[data-shop-filter]').forEach((button) => button.addEventListener('click', () => applyShopFilter(button.dataset.shopFilter as 'all' | DecorationSlot)));
  document.querySelectorAll<HTMLButtonElement>('[data-shop-item]').forEach((button) => button.addEventListener('click', () => handleShopItem(button.dataset.shopItem!)));
  document.querySelectorAll<HTMLButtonElement>('[data-customize-filter]').forEach((button) => button.addEventListener('click', () => applyCustomizeFilter(button.dataset.customizeFilter as DecorationSlot)));
  document.querySelectorAll<HTMLButtonElement>('[data-customize-item]').forEach((button) => button.addEventListener('click', () => handleCustomizeItem(button.dataset.customizeItem!)));
  updateShopEmptyState();
  document.querySelector('[data-export]')?.addEventListener('click', exportSave);
  document.querySelector<HTMLInputElement>('[data-import]')?.addEventListener('change', (event) => void importSave((event.currentTarget as HTMLInputElement).files?.[0]));
  document.querySelector('[data-delete-player]')?.addEventListener('click', () => {
    if (!player || !confirm(`Delete ${player.name}'s progress from this device? This cannot be undone unless you exported a backup.`)) return;
    deletePlayer(player.id); const remaining = listPlayers(); player = remaining[0] ?? null; if (player) setActivePlayer(player.id); screen = 'lobby'; if (player) closeModal(); else { modal = null; render(); }
  });
}

function showCatPage(page: number): void {
  const pageCount = catBookPages().length;
  stickerPage = Math.max(0, Math.min(page, pageCount - 1));
  const track = document.querySelector<HTMLElement>('[data-cat-slider-track]');
  if (track) track.style.transform = `translate3d(${-stickerPage * 100}%,0,0)`;
  document.querySelectorAll<HTMLElement>('[data-cat-page-status]').forEach((status) => { status.textContent = `Page ${stickerPage + 1} of ${pageCount}`; });
  document.querySelectorAll<HTMLElement>('[data-cat-slide]').forEach((slide) => slide.setAttribute('aria-hidden', String(Number(slide.dataset.catSlide) !== stickerPage)));
  document.querySelectorAll<HTMLButtonElement>('[data-cat-page]').forEach((button) => {
    const current = Number(button.dataset.catPage) === stickerPage;
    button.classList.toggle('current', current);
    button.setAttribute('aria-current', current ? 'page' : 'false');
  });
  document.querySelectorAll<HTMLButtonElement>('[data-cat-step]').forEach((button) => {
    button.disabled = stickerPage === 0 && button.dataset.catStep === '-1' || stickerPage === pageCount - 1 && button.dataset.catStep === '1';
  });
}

function switchPlayer(id: string): void {
  safeSave(); const next = loadPlayer(id); if (!next) return;
  player = next; setActivePlayer(id); feedback = 'none'; keypadValue = '';
  audio.applyPreference(player.settings.soundPreferred, player.settings.volume);
  screen = player.round?.status === 'complete' ? 'summary' : 'lobby'; closeModal();
}

function applyShopFilter(filter: 'all' | DecorationSlot): void {
  shopFilter = filter;
  document.querySelectorAll<HTMLButtonElement>('[data-shop-filter]').forEach((button) => {
    const selected = button.dataset.shopFilter === filter;
    button.classList.toggle('selected', selected);
    button.setAttribute('aria-pressed', String(selected));
  });
  document.querySelectorAll<HTMLElement>('[data-shop-card]').forEach((card) => card.classList.toggle('shop-hidden', filter !== 'all' && card.dataset.shopSlot !== filter));
  updateShopEmptyState();
  const dialog = document.querySelector<HTMLElement>('.shop-modal');
  const grid = document.querySelector<HTMLElement>('.shop-grid');
  if (dialog && grid) dialog.scrollTo({ top: Math.max(0, grid.offsetTop - 125), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
}

function updateShopEmptyState(): void {
  const empty = document.querySelector<HTMLElement>('[data-shop-empty]');
  if (!empty) return;
  const cards = [...document.querySelectorAll<HTMLElement>('[data-shop-card]')];
  const visibleCards = cards.filter((card) => !card.classList.contains('shop-hidden'));
  empty.hidden = visibleCards.length > 0;
  if (visibleCards.length) return;
  empty.textContent = cards.length
    ? `No new ${shopFilter === 'all' ? 'items' : DECORATION_SLOT_NAMES[shopFilter].toLowerCase()} left to collect.`
    : 'You own everything in the Café Shop. Your collection is ready in Customize Café.';
}

function updateShopControls(): void {
  if (!player) return;
  document.querySelectorAll<HTMLElement>('[data-token-balance]').forEach((balance) => { balance.textContent = String(player!.tokens); });
  for (const item of DECORATIONS) {
    const card = document.querySelector<HTMLElement>(`[data-shop-card="${item.id}"]`);
    const button = document.querySelector<HTMLButtonElement>(`[data-shop-item="${item.id}"]`);
    const status = card?.querySelector<HTMLElement>('[data-shop-status]');
    if (!card || !button || !status) continue;
    if (player.cosmetics.includes(item.id)) continue;
    const control = shopPurchaseControl(item);
    status.textContent = `🪙 ${item.price} Café Tokens`;
    button.textContent = control.label;
    button.className = `shop-action ${control.state}`;
    button.setAttribute('aria-label', control.ariaLabel);
    button.disabled = control.disabled || shopActionLocks.has(item.id);
  }
}

function handleShopItem(id: string): void {
  if (!player || shopActionLocks.has(id)) return;
  const decoration = decorationById(id);
  if (!decoration || player.cosmetics.includes(id)) return;
  shopActionLocks.add(id);
  const snapshot = { tokens: player.tokens, cosmetics: [...player.cosmetics], equipped: { ...player.equipped } };
  const result = purchaseDecoration(player, id);
  let message = result === 'insufficient' ? `You need ${decoration.price - player.tokens} more Café Tokens for ${decoration.name}.` : '';
  if (!safeSave()) {
    player.tokens = snapshot.tokens;
    player.cosmetics = snapshot.cosmetics;
    player.equipped = snapshot.equipped;
    message = 'That purchase could not be saved. Nothing was spent.';
  } else if (result === 'purchased') {
    audio.play('reward');
    message = `${decoration.name} is yours! Would you like to put it in your café now?`;
  }
  const live = document.querySelector<HTMLElement>('[data-shop-live]');
  if (live) { live.textContent = message; if (saveWarning) live.setAttribute('role', 'alert'); else live.removeAttribute('role'); }
  updateShopControls();
  if (result === 'purchased' && !saveWarning) {
    showPurchasePlacementPrompt(decoration);
    shopActionLocks.delete(id);
  } else window.setTimeout(() => { shopActionLocks.delete(id); updateShopControls(); }, 350);
}

function showPurchasePlacementPrompt(decoration: DecorationDefinition): void {
  const button = document.querySelector<HTMLButtonElement>(`[data-shop-item="${decoration.id}"]`);
  const card = button?.closest<HTMLElement>('[data-shop-card]');
  const status = card?.querySelector<HTMLElement>('[data-shop-status]');
  if (!button || !card || !status) return;
  card.classList.add('purchase-complete');
  status.textContent = 'Purchased!';
  const prompt = document.createElement('div');
  prompt.className = 'purchase-placement';
  prompt.setAttribute('role', 'group');
  prompt.setAttribute('aria-label', `Choose whether to show ${decoration.name}`);
  prompt.innerHTML = `<strong>Put it in your café now?</strong><div><button type="button" class="secondary" data-purchase-later="${decoration.id}">Not now</button><button type="button" class="primary" data-purchase-show="${decoration.id}">Yes, show it</button></div>`;
  button.replaceWith(prompt);
  prompt.querySelector<HTMLButtonElement>('[data-purchase-later]')?.addEventListener('click', () => finishPurchasePlacement(decoration.id, false));
  const showButton = prompt.querySelector<HTMLButtonElement>('[data-purchase-show]');
  showButton?.addEventListener('click', () => finishPurchasePlacement(decoration.id, true));
  showButton?.focus();
}

function finishPurchasePlacement(id: string, showNow: boolean): void {
  if (!player) return;
  const decoration = decorationById(id);
  const card = document.querySelector<HTMLElement>(`[data-shop-card="${id}"]`);
  if (!decoration || !card) return;
  if (showNow) {
    const equippedSnapshot = { ...player.equipped };
    player.equipped[decoration.slot] = id;
    if (!safeSave()) {
      player.equipped = equippedSnapshot;
      const live = document.querySelector<HTMLElement>('[data-shop-live]');
      if (live) { live.textContent = `${decoration.name} is yours, but the café could not be updated. Please try again.`; live.setAttribute('role', 'alert'); }
      return;
    }
  }
  const visibleCards = [...document.querySelectorAll<HTMLElement>('[data-shop-card]:not(.shop-hidden)')];
  const cardIndex = visibleCards.indexOf(card);
  const nextCard = (visibleCards[cardIndex + 1] ?? visibleCards[cardIndex - 1])?.querySelector<HTMLButtonElement>('.shop-action:not([disabled])');
  card.remove();
  updateShopEmptyState();
  const live = document.querySelector<HTMLElement>('[data-shop-live]');
  if (live) {
    live.textContent = showNow ? `${decoration.name} is now showing in your café.` : `${decoration.name} is saved in Customize Café.`;
    live.removeAttribute('role');
  }
  window.requestAnimationFrame(() => {
    const selectedFilter = document.querySelector<HTMLButtonElement>('[data-shop-filter][aria-pressed="true"]');
    (nextCard ?? document.querySelector<HTMLButtonElement>('[data-shop-card]:not(.shop-hidden) .shop-action:not([disabled])') ?? selectedFilter ?? document.querySelector<HTMLButtonElement>('[data-close-modal]'))?.focus();
  });
}

function applyCustomizeFilter(slot: DecorationSlot): void {
  customizeSlot = slot;
  document.querySelectorAll<HTMLButtonElement>('[data-customize-filter]').forEach((button) => {
    const selected = button.dataset.customizeFilter === slot;
    button.classList.toggle('selected', selected);
    button.setAttribute('aria-pressed', String(selected));
  });
  document.querySelectorAll<HTMLElement>('[data-customize-card]').forEach((card) => card.classList.toggle('shop-hidden', card.dataset.customizeSlot !== slot));
  updateCustomizeControls();
}

function updateCustomizeControls(): void {
  if (!player) return;
  for (const item of DECORATIONS) {
    const card = document.querySelector<HTMLElement>(`[data-customize-card="${item.id}"]`);
    const button = document.querySelector<HTMLButtonElement>(`[data-customize-item="${item.id}"]`);
    const status = card?.querySelector<HTMLElement>('[data-customize-status]');
    if (!card || !button || !status) continue;
    const showing = player.equipped[item.slot] === item.id;
    card.classList.toggle('showing', showing);
    status.textContent = showing ? 'Showing in your café' : 'In your collection';
    button.textContent = showing ? 'Put away' : 'Show';
    button.className = `shop-action ${showing ? 'showing' : 'owned'}`;
    button.setAttribute('aria-label', showing ? `Put away ${item.name}` : `Show ${item.name}`);
    button.disabled = customizeActionLocks.has(item.id);
  }
  const current = decorationById(player.equipped[customizeSlot]);
  const currentLabel = document.querySelector<HTMLElement>('[data-customize-current]');
  if (currentLabel) currentLabel.textContent = current?.slot === customizeSlot ? current.name : 'Nothing showing';
}

function handleCustomizeItem(id: string): void {
  if (!player || customizeActionLocks.has(id)) return;
  const decoration = decorationById(id);
  if (!decoration || !player.cosmetics.includes(id)) return;
  customizeActionLocks.add(id);
  const equippedSnapshot = { ...player.equipped };
  const result = toggleDecoration(player, id);
  let message = result === 'shown' ? `${decoration.name} is now showing in your café.` : `${decoration.name} was put away.`;
  if (!safeSave()) {
    player.equipped = equippedSnapshot;
    message = 'That change could not be saved. Your café was not changed.';
  }
  const live = document.querySelector<HTMLElement>('[data-customize-live]');
  if (live) { live.textContent = message; if (saveWarning) live.setAttribute('role', 'alert'); else live.removeAttribute('role'); }
  updateCustomizeControls();
  window.setTimeout(() => { customizeActionLocks.delete(id); updateCustomizeControls(); }, 350);
}

function exportSave(): void {
  if (!player) return;
  const blob = new Blob([JSON.stringify(player, null, 2)], { type: 'application/json' });
  const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `tables-cafe-${player.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'player'}.json`; link.click(); URL.revokeObjectURL(link.href);
}

async function importSave(file?: File): Promise<void> {
  if (!file || !player) return;
  try {
    const imported = validateImportedPlayer(JSON.parse(await file.text()), player.id);
    if (!confirm(`Replace ${player.name}'s save with the imported progress? Other players will not change.`)) return;
    savePlayer(imported);
    player = imported; audio.applyPreference(imported.settings.soundPreferred, imported.settings.volume); screen = imported.round?.status === 'complete' ? 'summary' : 'lobby'; showToast('Player save restored'); closeModal();
  } catch (error) { showToast(error instanceof Error ? error.message : 'That save could not be imported.'); }
}

initialize();
